'use client'

import { default as NextImage } from 'next/image'
import React from 'react'
import { cn } from '@/lib/ui/utils'
import { AspectRatio } from '@/services/components/strategies/common/Image/Image.types'

export type AllowedCropTypes =
  | 'c2_1'
  | 'c4_3'
  | 'c3_4'
  | 'c1_1'
  | 'c21_9'
  | 'c16_9'

export type ImageProps = {
  src?: string
  alt?: string
  backdrop?: boolean
  aspectRatio?: AspectRatio | number
  rounded?: boolean
  targetWidth?: number
  width?: number
  height?: number
  local?: boolean
  fallbackSrc?: string
  className?: string
  priority?: boolean
  zoom?: boolean
  cover?: boolean
  children?: React.ReactNode
}

/**
 * Transform the source URL to include the aspect ratio and width
 * @param src {string}
 * @param aspectRatio {string|number}
 * @param width {number}
 *
 * @returns {string} The transformed source URL
 */
const transformSource = (
  src: string,
  aspectRatio: AspectRatio | number,
  width: number,
): string => {
  const fullUrl: URL = new URL(
    `${src}${ratioToCUECrop(aspectRatio)}/${width}/image.webp`,
  )
  return fullUrl.pathname
}

const ratioToCUECrop = (aspectRatio: AspectRatio | number) => {
  switch (aspectRatio) {
    case AspectRatio.LANDSCAPE:
      return 'c16_9'
    case AspectRatio.PORTRAIT:
      return 'c3_4'
    case AspectRatio.WIDE:
      return 'c2_1'
    case AspectRatio.SQUARE:
      return 'c1_1'
    default:
      return 'c16_9'
  }
}

export const Image: React.FC<ImageProps> = ({
  src,
  alt = '',
  fallbackSrc = '/images/default.png',
  aspectRatio = AspectRatio.LANDSCAPE,
  targetWidth = 400,
  backdrop = true,
  rounded = true,
  local = false,
  zoom = false,
  cover = true,
  priority = false,
  className = 'w-[400px]',
  children,
}) => {
  const getSrc = () => {
    if (!src) {
      return fallbackSrc
    } else if (local) {
      return src
    }
    return transformSource(src, aspectRatio, targetWidth)
  }

  const fadeIn = (element: HTMLImageElement) => {
    element.classList.remove('opacity-0')
    element.style.opacity = '1'
    element.style.display = 'block'
  }

  return (
    <div
      className={cn(
        'group relative w-full overflow-hidden',
        rounded ? 'rounded-[8px]' : null,
        backdrop ? 'backdrop-brightness-90' : null,
        className,
      )}
      style={{
        aspectRatio: aspectRatio,
      }}
    >
      <NextImage
        fill
        alt={alt}
        priority={priority}
        onError={({ currentTarget }) => {
          currentTarget.onerror = null
          if (currentTarget.src !== fallbackSrc) {
            currentTarget.src = fallbackSrc
            currentTarget.srcset = fallbackSrc
            fadeIn(currentTarget)
          }
        }}
        onLoad={(event) => {
          fadeIn(event.currentTarget)
        }}
        loading={priority ? 'eager' : 'lazy'}
        className={cn(
          'opacity-0 transition-all duration-300',
          zoom ? 'relative inset-0 ease-in-out group-hover:scale-105' : null,
          cover ? 'object-cover' : null,
        )}
        loader={() => getSrc()}
        src="image.jpg"
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
      />
      {children}
    </div>
  )
}

export default Image
